import * as React from "react";
import styled, { css } from "styled-components";
import { useSelect, select } from "@hubper/theme";
import { FormLabelRow, FilterInput, Toggle } from "../../elements";
import { FormLabel } from "../../typo";

export const Base = styled.div<{ stretch?: boolean }>`
  width: ${p => (p.stretch ? "100%" : null)};
`;

export const Row = styled.div<{ seperated?: boolean }>`
  ${p =>
    p.seperated
      ? css`
          padding: ${select.space(2)};
          border-top: 1px solid ${select.color(["grey", 1])};
          &:last-child {
            border-bottom: 1px solid ${select.color(["grey", 1])};
          }
        `
      : css`
          &:not(:last-child) {
            margin-bottom: ${select.space(4)};
          }
        `}
`;

function filterItems(items: ToggleListItemProps[], searchStr: string) {
  return items.filter(item => {
    return item.name.toLowerCase().indexOf(searchStr) > -1;
  });
}

type ToggleListItemProps = {
  id: string;
  name: string;
};

type Props = {
  style?: React.CSSProperties;
  className?: string;
  stretch?: boolean;
  items: ToggleListItemProps[];
  selectedItems: string[];
  search?: boolean;
  searchPlaceholder?: string;
  onChange: (items: string[]) => void;
  toggleComponent?: React.ReactNode;
  seperated?: boolean;
  paddingY?: any;
};

function ToggleList(
  {
    style,
    className,
    items,
    selectedItems,
    onChange,
    stretch,
    search,
    searchPlaceholder,
    seperated,
    toggleComponent = <Toggle />,
    paddingY
  }: Props,
  ref: any
) {
  const { space } = useSelect();
  const [searchStr, setSearchStr] = React.useState<string>("");

  function handleToggle(id: string) {
    const isActive = selectedItems.some(x => x === id);

    const newValue = isActive
      ? selectedItems.filter(x => x !== id)
      : [...selectedItems, id];
    onChange(newValue);
  }

  return (
    <Base ref={ref} style={style} className={className} stretch={stretch}>
      {search && (
        <FilterInput
          style={{ marginBottom: space(5) }}
          placeholder={searchPlaceholder}
          value={searchStr}
          onChange={(evt: any) => setSearchStr(evt.target.value)}
        />
      )}
      {filterItems(items, searchStr).map(item => {
        const isActive = selectedItems.some(x => x === item.id);
        return (
          <Row
            key={item.id}
            seperated={seperated}
            style={
              paddingY
                ? { paddingTop: paddingY, paddingBottom: paddingY }
                : undefined
            }
          >
            <FormLabelRow
              stretch
              horizontalAlign="center"
              left={<FormLabel>{item.name}</FormLabel>}
              right={React.cloneElement(toggleComponent as any, {
                value: isActive,
                onClick: () => handleToggle(item.id)
              })}
            />
          </Row>
        );
      })}
    </Base>
  );
}

export default React.forwardRef(ToggleList);
